/**
 * Recover gracefully from a stale-deploy "module script failed" error.
 *
 * When the app is redeployed, Vite emits new content-hashed chunks and the old
 * ones disappear. A tab that is still running the previous bundle can then fail
 * to dynamically import a route chunk — on iOS Safari this surfaces as
 * "Importing a module script failed", which (with no error boundary) killed the
 * whole UI with React Router's developer screen.
 *
 * The correct recovery is simply to reload once so the browser fetches the new
 * asset graph. We guard with a short time window so a genuinely broken deploy
 * cannot cause an endless reload loop.
 */
const RELOAD_KEY = 'saik:chunk-reload-at';
const RELOAD_WINDOW_MS = 20000;

export function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: unknown })?.message ?? err ?? '');
  return /importing a module script failed|failed to fetch dynamically imported module|error loading dynamically imported module|dynamically imported module|chunkloaderror|loading chunk [\d]+ failed|unable to preload css/i.test(
    msg,
  );
}

/** Reload the page once for a chunk error; returns false when throttled. */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage may be unavailable (private mode) — reload anyway */
  }
  window.location.reload();
  return true;
}
